import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Avatar,
  IconButton,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ConfirmationNumber as TicketIcon,
  Event as EventIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  QrCode as QrCodeIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import QRCode from 'qrcode';

const COLORS = {
  primary: '#A8E6CF',
  secondary: '#88D8A3',
  accent: '#68C683',
  success: '#7ED321',
  background: '#F8FDF8',
  surface: '#FFFFFF',
  text: {
    primary: '#2C3E50',
    secondary: '#7F8C8D',
  }
};

const UserTickets = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const user = auth.currentUser;

      if (!user) {
        setError('Debes iniciar sesión para ver tus tickets');
        return;
      }

      // Consultar tickets del usuario actual
      const ticketsQuery = query(
        collection(db, 'eventTickets'),
        where('userId', '==', user.uid),
        orderBy('purchaseDate', 'desc')
      );

      const querySnapshot = await getDocs(ticketsQuery);
      const ticketsData = [];

      querySnapshot.forEach((doc) => {
        ticketsData.push({ id: doc.id, ...doc.data() });
      });

      setTickets(ticketsData);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      setError('Error al cargar los tickets');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Fecha no disponible';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch (error) {
      return 'Fecha inválida';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'purchased':
        return COLORS.success;
      case 'used':
        return COLORS.text.secondary;
      case 'cancelled':
        return '#EF5350';
      default:
        return COLORS.accent;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'purchased':
        return 'Válido';
      case 'used':
        return 'Usado';
      case 'cancelled':
        return 'Cancelado';
      default:
        return status;
    }
  };

  const generateQRCode = async (ticket) => {
    try {
      // Crear datos del QR con información del ticket
      const qrData = JSON.stringify({
        ticketNumber: ticket.ticketNumber,
        eventId: ticket.eventId,
        eventName: ticket.eventName,
        userId: ticket.userId,
        childName: ticket.childName,
        ticketCount: ticket.ticketCount,
        purchaseDate: ticket.purchaseDate?.seconds || Date.now() / 1000
      });

      // Generar QR code
      const qrCodeDataUrl = await QRCode.toDataURL(qrData, {
        width: 300,
        margin: 2,
        color: {
          dark: COLORS.text.primary,
          light: '#FFFFFF'
        }
      });

      setQrCodeUrl(qrCodeDataUrl);
    } catch (error) {
      console.error('Error generating QR code:', error);
    }
  };

  const handleViewTicket = async (ticket) => {
    setSelectedTicket(ticket);
    await generateQRCode(ticket);
    setDetailDialogOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailDialogOpen(false);
    setSelectedTicket(null);
    setQrCodeUrl('');
  };

  const handleDownloadTicket = () => {
    if (!qrCodeUrl || !selectedTicket) return;

    // Crear un enlace temporal para descargar
    const link = document.createElement('a');
    link.download = `ticket-${selectedTicket.ticketNumber}.png`;
    link.href = qrCodeUrl;
    link.click();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress sx={{ color: COLORS.accent }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (tickets.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 4,
          textAlign: 'center',
          backgroundColor: COLORS.background,
          borderRadius: 3
        }}
      >
        <TicketIcon sx={{ fontSize: 60, color: COLORS.text.secondary, mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No tienes tickets comprados
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Cuando compres entradas para eventos, aparecerán aquí
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {tickets.map((ticket) => (
          <Grid item xs={12} sm={6} md={4} key={ticket.id}>
            <Card
              sx={{
                height: '100%',
                borderRadius: 3,
                border: `2px solid ${getStatusColor(ticket.status)}20`,
                '&:hover': {
                  transform: 'translateY(-4px)',
                  transition: 'transform 0.3s ease-in-out',
                  boxShadow: `0 8px 25px ${getStatusColor(ticket.status)}30`
                }
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                {/* Header con número de ticket */}
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 2
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar
                      sx={{
                        bgcolor: getStatusColor(ticket.status) + '20',
                        color: getStatusColor(ticket.status),
                        width: 40,
                        height: 40
                      }}
                    >
                      <TicketIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Ticket
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}
                      >
                        {ticket.ticketNumber}
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={getStatusLabel(ticket.status)}
                    size="small"
                    sx={{
                      backgroundColor: getStatusColor(ticket.status),
                      color: 'white',
                      fontWeight: 'bold'
                    }}
                  />
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Información del evento */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <EventIcon sx={{ fontSize: 18, color: COLORS.accent }} />
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 'bold',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {ticket.eventName}
                    </Typography>
                  </Box>

                  {ticket.childName && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <PersonIcon sx={{ fontSize: 18, color: COLORS.text.secondary }} />
                      <Typography variant="body2" color="text.secondary">
                        {ticket.childName}
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CalendarIcon sx={{ fontSize: 18, color: COLORS.text.secondary }} />
                    <Typography variant="body2" color="text.secondary">
                      Comprado: {formatDate(ticket.purchaseDate)}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MoneyIcon sx={{ fontSize: 18, color: COLORS.success }} />
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: COLORS.success }}>
                      {ticket.price?.toFixed(2)}€
                      {ticket.ticketCount > 1 && ` × ${ticket.ticketCount}`}
                    </Typography>
                  </Box>
                </Box>

                {/* Botón para ver ticket */}
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<QrCodeIcon />}
                  onClick={() => handleViewTicket(ticket)}
                  disabled={ticket.status === 'cancelled'}
                  sx={{
                    borderColor: COLORS.accent,
                    color: COLORS.accent,
                    '&:hover': {
                      borderColor: COLORS.secondary,
                      backgroundColor: COLORS.primary + '20'
                    }
                  }}
                >
                  {isMobile ? 'Ver QR' : 'Ver Ticket QR'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Dialog de detalle del ticket */}
      <Dialog
        open={detailDialogOpen}
        onClose={handleCloseDetail}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TicketIcon sx={{ color: COLORS.accent }} />
              <Typography variant={isMobile ? "subtitle1" : "h6"}>
                Detalle del Ticket
              </Typography>
            </Box>
            <IconButton onClick={handleCloseDetail} size={isMobile ? "small" : "medium"}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {selectedTicket && (
            <Box>
              {/* Información del ticket */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  backgroundColor: COLORS.background,
                  borderRadius: 2,
                  mb: 3
                }}
              >
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  {selectedTicket.eventName}
                </Typography>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Número de ticket:</strong> {selectedTicket.ticketNumber}
                  </Typography>
                  {selectedTicket.childName && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Nombre:</strong> {selectedTicket.childName}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Cantidad:</strong> {selectedTicket.ticketCount} entrada(s)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Precio:</strong> {selectedTicket.price?.toFixed(2)}€
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Fecha de compra:</strong> {formatDate(selectedTicket.purchaseDate)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" component="div">
                    <strong>Estado:</strong>{' '}
                    <Chip
                      label={getStatusLabel(selectedTicket.status)}
                      size="small"
                      sx={{
                        backgroundColor: getStatusColor(selectedTicket.status),
                        color: 'white',
                        fontWeight: 'bold',
                        ml: 1
                      }}
                    />
                  </Typography>
                </Box>
              </Paper>

              {/* Código QR */}
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Código QR de entrada
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Presenta este código en el evento
                </Typography>

                {qrCodeUrl ? (
                  <Paper
                    elevation={3}
                    sx={{
                      display: 'inline-block',
                      p: 2,
                      backgroundColor: 'white',
                      borderRadius: 2
                    }}
                  >
                    <img
                      src={qrCodeUrl}
                      alt="QR Code"
                      style={{ width: '100%', maxWidth: 300, height: 'auto' }}
                    />
                  </Paper>
                ) : (
                  <CircularProgress sx={{ color: COLORS.accent }} />
                )}
              </Box>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  💡 <strong>Consejo:</strong> Descarga el ticket o haz una captura de pantalla
                  para tenerlo disponible sin conexión
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDetail}>
            Cerrar
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTicket}
            sx={{
              backgroundColor: COLORS.accent,
              '&:hover': { backgroundColor: COLORS.secondary }
            }}
          >
            Descargar QR
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserTickets;
